import {
  CalendarClock,
  CircleCheck,
  HardDrive,
  Radio,
  RefreshCw,
  TriangleAlert,
  Users,
  VideoOff,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AdminStatsResponse } from '@hiredude/meet-shared';

import {
  errorMessage,
  isSessionExpired,
  REQUEST_TIMEOUT_MS,
  withTimeout,
} from '@/components/admin/adminError';
import { formatBytes, formatCount } from '@/components/admin/format';
import {
  MEETING_STATUSES,
  MEETING_STATUS_META,
  RECORDING_STATUSES,
  RECORDING_STATUS_META,
  StatusDot,
  type BadgeVariant,
} from '@/components/admin/status';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { adminStats } from '@/lib/adminApi';
import { cn } from '@/lib/utils';

type Tone = 'neutral' | 'success' | 'warning' | 'danger';

const TONE_RING: Record<Tone, string> = {
  neutral: 'border-border/70',
  success: 'border-[color-mix(in_oklab,var(--success)_30%,transparent)]',
  warning: 'border-[color-mix(in_oklab,var(--warning)_35%,transparent)]',
  danger: 'border-[color-mix(in_oklab,var(--destructive)_40%,transparent)]',
};

const TONE_ICON: Record<Tone, string> = {
  neutral: 'bg-white/[0.06] text-muted-foreground',
  success:
    'bg-[color-mix(in_oklab,var(--success)_14%,transparent)] text-[var(--success)]',
  warning:
    'bg-[color-mix(in_oklab,var(--warning)_14%,transparent)] text-[var(--warning)]',
  danger:
    'bg-[color-mix(in_oklab,var(--destructive)_16%,transparent)] text-[color-mix(in_oklab,var(--destructive)_80%,white)]',
};

const TONE_VALUE: Record<Tone, string> = {
  neutral: 'text-foreground',
  success: 'text-[var(--success)]',
  warning: 'text-[var(--warning)]',
  danger: 'text-[color-mix(in_oklab,var(--destructive)_82%,white)]',
};

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    let ignore = false;
    setLoading(true);

    withTimeout(adminStats(), REQUEST_TIMEOUT_MS, 'The server took too long to respond.')
      .then((result) => {
        if (ignore) return;
        setStats(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (isSessionExpired(err)) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setError(errorMessage(err, 'Could not load the dashboard.'));
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [reloadToken, navigate]);

  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6 lg:p-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm text-pretty">
            Everything scheduled, live and recorded across Hiredude Meet.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
          {loading && stats ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
          Refresh
        </Button>
      </header>

      <div className="mt-6">
        {!stats && loading && <DashboardSkeleton />}

        {!stats && !loading && error && (
          <Card className={cn('p-6', TONE_RING.danger)}>
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <span
                className={cn('grid size-11 shrink-0 place-items-center rounded-xl', TONE_ICON.danger)}
              >
                <TriangleAlert className="size-5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold tracking-tight">Could not load the dashboard</h2>
                <p className="text-muted-foreground mt-1 text-sm text-pretty" role="alert">
                  {error}
                </p>
              </div>
              <Button variant="outline" onClick={reload} className="w-full sm:w-auto">
                <RefreshCw className="size-4" />
                Retry
              </Button>
            </div>
          </Card>
        )}

        {stats && (
          <div className={cn('space-y-4 transition-opacity', loading && 'opacity-60')}>
            {error && (
              <p
                role="alert"
                className="rounded-xl border border-[color-mix(in_oklab,var(--warning)_35%,transparent)] bg-[color-mix(in_oklab,var(--warning)_12%,transparent)] px-3.5 py-2.5 text-sm text-[var(--warning)]"
              >
                {error} Showing the last figures we loaded.
              </p>
            )}

            <StatGrid stats={stats} />

            <div className="grid gap-4 lg:grid-cols-2">
              <Panel title="Meetings by status">
                <ul className="space-y-1">
                  {MEETING_STATUSES.map((status) => {
                    const meta = MEETING_STATUS_META[status];
                    return (
                      <BreakdownRow
                        key={status}
                        variant={meta.variant}
                        label={meta.label}
                        value={stats.meetings[status]}
                        to={`/admin/meetings?status=${status}`}
                      />
                    );
                  })}
                </ul>
              </Panel>

              <Panel title="Recordings by status">
                <ul className="space-y-1">
                  {RECORDING_STATUSES.map((status) => {
                    const meta = RECORDING_STATUS_META[status];
                    return (
                      <BreakdownRow
                        key={status}
                        variant={meta.variant}
                        label={meta.label}
                        value={stats.recordings[status]}
                      />
                    );
                  })}
                </ul>
              </Panel>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Panel title="Recorded storage">
                <div className="flex items-center gap-4">
                  <span
                    className={cn(
                      'grid size-11 shrink-0 place-items-center rounded-xl',
                      TONE_ICON.neutral,
                    )}
                  >
                    <HardDrive className="size-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold tracking-tight">
                      {formatBytes(stats.recordedBytes)}
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-xs text-pretty">
                      Across {formatCount(stats.recordings.completed)} completed recordings.
                    </p>
                  </div>
                </div>
              </Panel>

              <Panel title="Webhook queue">
                <div className="flex flex-wrap gap-6">
                  <div>
                    <p className="text-xl font-semibold tracking-tight">
                      {formatCount(stats.webhookQueue.pending)}
                    </p>
                    <p className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-xs">
                      <Webhook className="size-3" aria-hidden />
                      Pending
                    </p>
                  </div>
                  <Separator orientation="vertical" className="h-auto self-stretch" />
                  <div>
                    <p
                      className={cn(
                        'text-xl font-semibold tracking-tight',
                        stats.webhookQueue.failed > 0 ? TONE_VALUE.warning : TONE_VALUE.neutral,
                      )}
                    >
                      {formatCount(stats.webhookQueue.failed)}
                    </p>
                    <p
                      className={cn(
                        'mt-0.5 flex items-center gap-1.5 text-xs',
                        stats.webhookQueue.failed > 0
                          ? 'text-[var(--warning)]'
                          : 'text-muted-foreground',
                      )}
                    >
                      <TriangleAlert className="size-3" aria-hidden />
                      Failed
                    </p>
                  </div>
                </div>
                {stats.webhookQueue.failed > 0 && (
                  <p className="text-muted-foreground mt-3 text-xs text-pretty">
                    Hiredude has not been told about these meetings. They need a retry.
                  </p>
                )}
              </Panel>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatGrid({ stats }: { stats: AdminStatsResponse }) {
  const missingRecordings = stats.endedWithoutRecording;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Total meetings"
        value={formatCount(stats.totalMeetings)}
        hint="Every interview ever scheduled."
        icon={Users}
        to="/admin/meetings"
      />
      <StatCard
        label="Live now"
        value={formatCount(stats.meetings.live)}
        hint={stats.meetings.live > 0 ? 'Interviews in progress right now.' : 'Nothing in progress.'}
        icon={Radio}
        tone={stats.meetings.live > 0 ? 'success' : 'neutral'}
        to="/admin/meetings?status=live"
      />
      <StatCard
        label="Scheduled today"
        value={formatCount(stats.upcomingToday)}
        hint="Starting later today."
        icon={CalendarClock}
        to="/admin/meetings?status=scheduled"
      />
      <StatCard
        label="Ended without recording"
        value={formatCount(missingRecordings)}
        hint={
          missingRecordings > 0
            ? 'These interviews ended with no recording and cannot be recovered.'
            : 'Every ended interview has its recording.'
        }
        icon={missingRecordings > 0 ? VideoOff : CircleCheck}
        tone={missingRecordings > 0 ? 'danger' : 'success'}
        to="/admin/meetings?status=ended"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'neutral',
  to,
}: {
  label: string;
  value: string;
  hint: string;
  icon: LucideIcon;
  tone?: Tone;
  to?: string;
}) {
  const body = (
    <Card
      className={cn(
        'flex h-full flex-col gap-3 p-5 transition-[border-color,box-shadow]',
        TONE_RING[tone],
        to && 'group-hover:border-white/25 group-hover:shadow-[var(--shadow-lift)]',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-muted-foreground text-[11px] font-semibold tracking-[0.09em] uppercase">
          {label}
        </p>
        <span className={cn('grid size-9 shrink-0 place-items-center rounded-xl', TONE_ICON[tone])}>
          <Icon className="size-4.5" aria-hidden />
        </span>
      </div>

      <p className={cn('text-3xl leading-none font-semibold tracking-tight', TONE_VALUE[tone])}>
        {value}
      </p>

      <p className="text-muted-foreground mt-auto text-xs text-pretty">{hint}</p>
    </Card>
  );

  if (!to) return body;

  return (
    <Link to={to} className="group block rounded-2xl">
      {body}
    </Link>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="p-5">
      <h2 className="text-muted-foreground text-[11px] font-semibold tracking-[0.09em] uppercase">
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </Card>
  );
}

function BreakdownRow({
  variant,
  label,
  value,
  to,
}: {
  variant: BadgeVariant;
  label: string;
  value: number;
  to?: string;
}) {
  const content = (
    <>
      <StatusDot variant={variant} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="text-foreground shrink-0 font-medium tabular-nums">
        {formatCount(value)}
      </span>
    </>
  );

  return (
    <li>
      {to ? (
        <Link
          to={to}
          className="text-muted-foreground hover:text-foreground flex min-h-11 items-center gap-3 rounded-lg px-2 text-sm transition-colors hover:bg-white/[0.05]"
        >
          {content}
        </Link>
      ) : (
        <div className="text-muted-foreground flex min-h-11 items-center gap-3 px-2 text-sm">
          {content}
        </div>
      )}
    </li>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading dashboard">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((index) => (
          <Card key={index} className="space-y-3 p-5">
            <div className="flex items-start justify-between gap-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="size-9 rounded-xl" />
            </div>
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-full" />
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {[0, 1].map((index) => (
          <Card key={index} className="p-5">
            <Skeleton className="h-3 w-32" />
            <div className="mt-4 space-y-3">
              {[0, 1, 2, 3].map((row) => (
                <Skeleton key={row} className="h-6 w-full" />
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
